export type GuideQuestionType =
  | "single"
  | "multiple"
  | "trueFalse"
  | "longText"
  | "text"
  | "number"
  | "yesNo"
  | "confirmation"
  | "photo";
export type GuideQuestionFormMode = "feedback" | "score";

export interface GuideQuestionOption {
  id: string;
  text: string;
  isCorrect?: boolean | null;
}

export interface GuideQuestion {
  id: string;
  type: GuideQuestionType;
  text: string;
  instructions?: string | null;
  correctiveMessage?: string | null;
  options?: GuideQuestionOption[] | null;
  required: boolean;
  acceptedOptionId?: string | null;
  minValue?: number | null;
  maxValue?: number | null;
  unit?: string | null;
}

export interface GuideFieldValidation {
  questionId: string;
  passed: boolean;
  message?: string | null;
}

export interface GuideQuestionForm {
  enabled: boolean;
  title?: string | null;
  mode: GuideQuestionFormMode;
  questions: GuideQuestion[];
}

export interface GuideQuestionAnswer {
  questionId: string;
  optionIds?: string[];
  text?: string;
  numberValue?: number | null;
  photoUrl?: string;
  confirmed?: boolean;
}

export interface GuideQuestionSubmitResult {
  id: string;
  validationPassed?: boolean | null;
  passed?: boolean | null;
  validationMessage?: string | null;
  fieldResults?: GuideFieldValidation[];
}

export interface GuideQuestionResponseRequest {
  flowId: string;
  formKey: string;
  stepIndex?: number | null;
  sessionId?: string | null;
  flowVersion?: number | null;
  answers: GuideQuestionAnswer[];
}

export interface GuideQuestionResponse {
  id: string;
  flowId: string;
  flowVersion?: number | null;
  sessionId?: string | null;
  formKey: string;
  stepIndex?: number | null;
  stepTitle?: string | null;
  formTitle?: string | null;
  userEmail?: string | null;
  userName?: string | null;
  mode: GuideQuestionFormMode;
  answers: GuideQuestionAnswer[];
  questions: GuideQuestion[];
  score?: number | null;
  maxScore?: number | null;
  validationPassed?: boolean | null;
  validationMessage?: string | null;
  fieldResults?: GuideFieldValidation[];
  submittedAtUtc?: string | null;
}

export const GUIDE_END_FORM_KEY = "end";
export const GUIDE_QUESTION_LIMITS = {
  maxQuestions: 10,
  minOptions: 2,
  maxOptions: 6,
  maxTextLength: 500,
  maxOptionTextLength: 200,
  maxAnswerTextLength: 2000,
  maxTitleLength: 120,
};

export const GUIDE_QUESTION_TYPE_LABELS: Record<GuideQuestionType, string> = {
  text: "Text",
  number: "Number",
  yesNo: "Yes / No",
  single: "Single choice",
  confirmation: "Confirmation checkbox",
  photo: "Photo",
  multiple: "Multiple choice",
  trueFalse: "True / False",
  longText: "Long answer",
};

const KNOWN_QUESTION_TYPES: GuideQuestionType[] = [
  "single",
  "multiple",
  "trueFalse",
  "longText",
  "text",
  "number",
  "yesNo",
  "confirmation",
  "photo",
];

export function questionHasOptions(type: GuideQuestionType): boolean {
  return type === "single" || type === "multiple" || type === "trueFalse" || type === "yesNo";
}

export function newQuestionId(): string {
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID().replace(/-/g, "");
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
}

export function yesNoOptions(correct?: string | null): GuideQuestionOption[] {
  return [
    { id: "yes", text: "Yes", isCorrect: correct === "yes" ? true : null },
    { id: "no", text: "No", isCorrect: correct === "no" ? true : null },
  ];
}

export function trueFalseOptions(correct?: string | null): GuideQuestionOption[] {
  return [
    { id: "true", text: "True", isCorrect: correct === "true" ? true : null },
    { id: "false", text: "False", isCorrect: correct === "false" ? true : null },
  ];
}

export function createQuestion(type: GuideQuestionType = "single"): GuideQuestion {
  const q: GuideQuestion = { id: newQuestionId(), type, text: "", required: false };
  if (type === "trueFalse") q.options = trueFalseOptions();
  else if (type === "yesNo") q.options = yesNoOptions();
  else if (questionHasOptions(type)) {
    q.options = [
      { id: newQuestionId(), text: "" },
      { id: newQuestionId(), text: "" },
    ];
  }
  return q;
}

export function createEmptyForm(): GuideQuestionForm {
  return { enabled: true, title: "", mode: "feedback", questions: [createQuestion()] };
}

/** Accepts a form from the API, local storage or a JSON string and returns a well-formed copy (or null). */
export function parseGuideQuestionForm(raw: any): GuideQuestionForm | null {
  if (raw == null || raw === "") return null;
  let src = raw;
  if (typeof src === "string") {
    try {
      src = JSON.parse(src);
    } catch {
      return null;
    }
  }
  if (typeof src !== "object") return null;
  const pick = (o: any, camel: string) => o?.[camel] ?? o?.[camel.charAt(0).toUpperCase() + camel.slice(1)];
  const questions: GuideQuestion[] = (pick(src, "questions") || [])
    .filter((q: any) => q && typeof q === "object")
    .map((q: any) => {
      const rawType = pick(q, "type");
      const type = (KNOWN_QUESTION_TYPES.includes(rawType) ? rawType : "single") as GuideQuestionType;
      const options = Array.isArray(pick(q, "options"))
        ? pick(q, "options").map((o: any) => ({
            id: String(pick(o, "id") || newQuestionId()),
            text: String(pick(o, "text") ?? ""),
            isCorrect: pick(o, "isCorrect") === true ? true : null,
          }))
        : null;
      const min = pick(q, "minValue");
      const max = pick(q, "maxValue");
      return {
        id: String(pick(q, "id") || newQuestionId()),
        type,
        text: String(pick(q, "text") ?? ""),
        instructions: pick(q, "instructions") ?? "",
        correctiveMessage: pick(q, "correctiveMessage") ?? "",
        options: questionHasOptions(type) ? options : null,
        required: pick(q, "required") === true,
        acceptedOptionId: pick(q, "acceptedOptionId") || null,
        minValue: min === null || min === undefined || min === "" ? null : Number(min),
        maxValue: max === null || max === undefined || max === "" ? null : Number(max),
        unit: pick(q, "unit") ?? "",
      } as GuideQuestion;
    });
  return {
    enabled: pick(src, "enabled") === true,
    title: pick(src, "title") ?? "",
    mode: pick(src, "mode") === "score" ? "score" : "feedback",
    questions,
  };
}

export function isGuideQuestionFormActive(form: GuideQuestionForm | null | undefined): boolean {
  return !!form && form.enabled && Array.isArray(form.questions) && form.questions.length > 0;
}

export function guideQuestionCount(raw: any): number {
  const form = parseGuideQuestionForm(raw);
  return isGuideQuestionFormActive(form) ? form!.questions.length : 0;
}

/** Problems that would make the server drop a question; empty when the form is ready to save. */
export function guideQuestionFormErrors(form: GuideQuestionForm | null | undefined): string[] {
  if (!form || !form.enabled) return [];
  const errors: string[] = [];
  if (!form.questions.length) errors.push("Add at least one question or turn questions off.");
  form.questions.forEach((q, i) => {
    const n = i + 1;
    if (!q.text.trim()) errors.push(`Question ${n}: enter the question text.`);
    if (q.type === "single" || q.type === "multiple") {
      const filled = (q.options || []).filter((o) => o.text.trim());
      if (filled.length < GUIDE_QUESTION_LIMITS.minOptions) {
        errors.push(`Question ${n}: add at least ${GUIDE_QUESTION_LIMITS.minOptions} options.`);
      }
    }
    const scoredChoice = q.type === "single" || q.type === "multiple" || q.type === "trueFalse" || q.type === "yesNo";
    if (form.mode === "score" && scoredChoice && !(q.options || []).some((o) => o.isCorrect && (q.type === "trueFalse" || q.type === "yesNo" || o.text.trim()))) {
      errors.push(`Question ${n}: mark the accepted answer.`);
    }
  });
  return errors;
}

/** Copy for saving: trims text, drops empty options and correct marks in feedback mode; null when disabled and empty. */
export function cleanGuideQuestionForm(form: GuideQuestionForm | null | undefined): GuideQuestionForm | null {
  if (!form) return null;
  const isScore = form.mode === "score";
  const questions = form.questions
    .filter((q) => q.text.trim())
    .slice(0, GUIDE_QUESTION_LIMITS.maxQuestions)
    .map((q) => {
      const accepted =
        q.type === "multiple"
          ? null
          : q.acceptedOptionId ||
            (isScore ? (q.options || []).find((o) => o.isCorrect)?.id : null) ||
            null;
      let min = q.minValue == null || Number.isNaN(Number(q.minValue)) ? null : Number(q.minValue);
      let max = q.maxValue == null || Number.isNaN(Number(q.maxValue)) ? null : Number(q.maxValue);
      if (min != null && max != null && min > max) {
        const swap = min;
        min = max;
        max = swap;
      }
      return {
        id: q.id,
        type: q.type,
        text: q.text.trim().slice(0, GUIDE_QUESTION_LIMITS.maxTextLength),
        instructions: (q.instructions || "").trim().slice(0, GUIDE_QUESTION_LIMITS.maxTextLength) || null,
        correctiveMessage: (q.correctiveMessage || "").trim().slice(0, GUIDE_QUESTION_LIMITS.maxTextLength) || null,
        required: !!q.required,
        acceptedOptionId: questionHasOptions(q.type) ? accepted : null,
        minValue: q.type === "number" ? min : null,
        maxValue: q.type === "number" ? max : null,
        unit: q.type === "number" ? (q.unit || "").trim().slice(0, 40) || null : null,
        options: questionHasOptions(q.type)
          ? (q.options || [])
              .filter((o) => q.type === "trueFalse" || q.type === "yesNo" || o.text.trim())
              .slice(0, GUIDE_QUESTION_LIMITS.maxOptions)
              .map((o) => ({
                id: o.id,
                text: o.text.trim().slice(0, GUIDE_QUESTION_LIMITS.maxOptionTextLength),
                isCorrect:
                  q.type === "multiple"
                    ? isScore && o.isCorrect
                      ? true
                      : null
                    : o.id === accepted
                      ? true
                      : null,
              }))
          : null,
      };
    });
  if (!form.enabled && !questions.length) return null;
  return {
    enabled: !!form.enabled,
    title: (form.title || "").trim().slice(0, GUIDE_QUESTION_LIMITS.maxTitleLength),
    mode: isScore ? "score" : "feedback",
    questions,
  };
}

export function isQuestionAnswered(q: GuideQuestion, answer: GuideQuestionAnswer | undefined): boolean {
  if (!answer) return false;
  if (q.type === "longText" || q.type === "text") return !!answer.text?.trim();
  if (q.type === "number") return answer.numberValue !== null && answer.numberValue !== undefined && answer.numberValue !== ("" as any) && !Number.isNaN(Number(answer.numberValue));
  if (q.type === "photo") return !!answer.photoUrl;
  if (q.type === "confirmation") return answer.confirmed === true;
  const valid = new Set((q.options || []).map((o) => o.id));
  return (answer.optionIds || []).some((id) => valid.has(id));
}
